import React, { useState, useEffect } from 'react';
import '../styles/ManageTournaments.css';
import TimeInput from './TimeInput';

function ManageTournaments() {
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importResults, setImportResults] = useState([]);
  const [selectedImports, setSelectedImports] = useState({});
  const [importLoading, setImportLoading] = useState(false);
  const [selectedTournamentVolunteers, setSelectedTournamentVolunteers] = useState(null);
  const [editingTournament, setEditingTournament] = useState(null);
  const [availableVolunteers, setAvailableVolunteers] = useState([]);
  const [selectedVolunteer, setSelectedVolunteer] = useState('');
  const [volunteerComment, setVolunteerComment] = useState('');
  const [formData, setFormData] = useState({
    tournament_name: '',
    start_date: '',
    end_date: '',
    venue: '',
    start_time: '',
    end_time: '',
    status: 'Upcoming',
    tournament_url: ''
  });

  useEffect(() => {
    fetchTournaments();
    fetchAvailableVolunteers();
  }, []);

  const fetchAvailableVolunteers = async () => {
    try {
      const response = await fetch('/api/volunteers/available', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        }
      });
      const result = await response.json();
      if (result.success && result.data) {
        setAvailableVolunteers(result.data);
      }
    } catch (err) {
      console.error('Error fetching available volunteers:', err);
    }
  };

  const fetchTournaments = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/tournaments/with-volunteers');
      const result = await response.json();
      if (result.success && result.data) {
        setTournaments(result.data);
      }
    } catch (err) {
      console.error('Error fetching tournaments:', err);
      setMessage('Failed to load tournaments');
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddTournament = async (e) => {
    e.preventDefault();
    
    if (!formData.tournament_name || !formData.start_date) {
      setMessage('Tournament name and start date are required');
      setMessageType('error');
      return;
    }

    try {
      setLoading(true);
      setMessage('');

      // Add tournament to Google Sheets
      const response = await fetch('/api/tournaments', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        },
        body: JSON.stringify({
          'Tournament Name': formData.tournament_name,
          'Start Date': formData.start_date,
          'End Date': formData.end_date || formData.start_date,
          'Venue': formData.venue,
          'Start Time': formData.start_time,
          'End Time': formData.end_time,
          'Status': formData.status,
          'Tournament URL': formData.tournament_url
        })
      });

      const result = await response.json();

      if (result.success) {
        setMessage('✅ Tournament added successfully');
        setMessageType('success');
        setFormData({
          tournament_name: '',
          start_date: '',
          end_date: '',
          venue: '',
          start_time: '',
          end_time: '',
          status: 'Upcoming',
          tournament_url: ''
        });
        setShowAddForm(false);
        fetchTournaments();
      } else {
        setMessage(`❌ ${result.message}`);
        setMessageType('error');
      }
    } catch (err) {
      setMessage('Failed to add tournament: ' + err.message);
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTournament = async (tournamentName) => {
    if (!window.confirm(`Are you sure you want to delete tournament "${tournamentName}"? This cannot be undone.`)) {
      return;
    }

    try {
      setLoading(true);
      setMessage('');

      const response = await fetch('/api/tournaments/delete', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        },
        body: JSON.stringify({ tournament_name: tournamentName })
      });

      const result = await response.json();

      if (result.success) {
        setMessage('✅ Tournament deleted successfully');
        setMessageType('success');
        fetchTournaments();
      } else {
        setMessage(`❌ ${result.message || 'Failed to delete tournament'}`);
        setMessageType('error');
      }
    } catch (err) {
      setMessage('Failed to delete tournament: ' + err.message);
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const handleImportFromBadmintonSweden = async () => {
    try {
      setImportLoading(true);
      setShowImportModal(true);
      setImportResults([]);
      setSelectedImports({});
      setMessage('');

      const response = await fetch('/api/tournaments/search-open', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        }
      });

      const result = await response.json();

      if (result.success) {
        setImportResults(result.tournaments || []);
      } else {
        setMessage(`❌ ${result.error || 'Failed to search tournaments'}`);
        setMessageType('error');
        setShowImportModal(false);
      }
    } catch (err) {
      setMessage('Failed to search tournaments: ' + err.message);
      setMessageType('error');
      setShowImportModal(false);
    } finally {
      setImportLoading(false);
    }
  };

  const toggleImportSelect = (idx) => {
    setSelectedImports(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  const toggleImportSelectAll = () => {
    const allSelected = importResults.length > 0 && importResults.every((_, i) => selectedImports[i]);
    if (allSelected) {
      setSelectedImports({});
    } else {
      const next = {};
      importResults.forEach((_, i) => { next[i] = true; });
      setSelectedImports(next);
    }
  };

  const handleConfirmImport = async () => {
    const chosen = importResults.filter((_, i) => selectedImports[i]);
    if (chosen.length === 0) {
      setMessage('Please select at least one tournament');
      setMessageType('error');
      return;
    }

    try {
      setImportLoading(true);
      const response = await fetch('/api/tournaments/bulk-add', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        },
        body: JSON.stringify({ tournaments: chosen })
      });
      const result = await response.json();
      if (result.success) {
        setMessage(`✅ Added ${result.added} tournament(s)${result.skipped > 0 ? `, skipped ${result.skipped} existing` : ''}`);
        setMessageType('success');
        setShowImportModal(false);
        fetchTournaments();
      } else {
        setMessage(`❌ ${result.message || 'Failed to import'}`);
        setMessageType('error');
      }
    } catch (err) {
      setMessage('Failed to import: ' + err.message);
      setMessageType('error');
    } finally {
      setImportLoading(false);
    }
  };

  const handleRepairSheet = async () => {
    if (!window.confirm('This will repair the tournament sheet structure. Continue?')) {
      return;
    }

    try {
      setLoading(true);
      setMessage('🔧 Repairing tournament sheet...');
      setMessageType('');

      const response = await fetch('/api/tournaments/repair', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        }
      });

      const result = await response.json();

      if (result.success) {
        setMessage(`✅ ${result.message}`);
        setMessageType('success');
        fetchTournaments();
      } else {
        setMessage(`❌ ${result.message}`);
        setMessageType('error');
      }
    } catch (err) {
      setMessage('Failed to repair sheet: ' + err.message);
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const handleEditTournament = (tournament) => {
    setEditingTournament(tournament['Tournament Name']);
    setFormData({
      tournament_name: tournament['Tournament Name'],
      start_date: tournament['Start Date'],
      end_date: tournament['End Date'],
      venue: tournament.Venue || '',
      start_time: tournament['Start Time'] || '',
      end_time: tournament['End Time'] || '',
      status: tournament.Status || 'Upcoming'
    });
    setShowAddForm(true);
  };

  const handleAddVolunteerToTournament = async () => {
    if (!selectedVolunteer || !editingTournament) {
      setMessage('Please select a volunteer');
      setMessageType('error');
      return;
    }

    try {
      setLoading(true);
      const response = await fetch('/api/tournaments/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        },
        body: JSON.stringify({
          volunteer_name: selectedVolunteer,
          tournament_name: editingTournament,
          comments: volunteerComment
        })
      });

      const result = await response.json();

      if (result.success) {
        setMessage(`✅ ${selectedVolunteer} added to tournament`);
        setMessageType('success');
        setSelectedVolunteer('');
        setVolunteerComment('');
        fetchTournaments();
      } else {
        setMessage(`❌ ${result.message}`);
        setMessageType('error');
      }
    } catch (err) {
      setMessage('Error adding volunteer: ' + err.message);
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveVolunteerFromTournament = async (volunteerName) => {
    if (!window.confirm(`Remove ${volunteerName} from ${editingTournament}?`)) {
      return;
    }

    try {
      setLoading(true);
      const response = await fetch('/api/tournaments/unregister', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        },
        body: JSON.stringify({
          volunteer_name: volunteerName,
          tournament_name: editingTournament
        })
      });

      const result = await response.json();

      if (result.success) {
        setMessage(`✅ ${volunteerName} removed from tournament`);
        setMessageType('success');
        fetchTournaments();
      } else {
        setMessage(`❌ ${result.message}`);
        setMessageType('error');
      }
    } catch (err) {
      setMessage('Error removing volunteer: ' + err.message);
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="manage-tournaments-container">
      <h2>Manage Tournaments</h2>
      <p className="section-description">
        View, add, or manage tournaments. Volunteers can register their support for these tournaments.
      </p>

      {message && (
        <div className={`alert alert-${messageType}`}>
          <span>{message}</span>
          {messageType === 'error' && (
            <button onClick={() => setMessage('')}>×</button>
          )}
        </div>
      )}

      <button 
        className="btn-add-tournament"
        onClick={() => setShowAddForm(!showAddForm)}
        disabled={loading}
      >
        {showAddForm ? '✕ Cancel' : '+ Add Tournament'}
      </button>

      <button 
        className="btn-import-tournament"
        onClick={handleImportFromBadmintonSweden}
        disabled={loading}
        title="Import tournaments from Badminton Sweden"
      >
        🔄 Import from Badminton Sweden
      </button>

      <button 
        className="btn-repair-sheet"
        onClick={handleRepairSheet}
        disabled={loading}
        title="Repair tournament sheet structure"
      >
        🔧 Repair Sheet
      </button>

      {selectedTournamentVolunteers && (
        <div className="volunteer-modal-overlay" onClick={() => setSelectedTournamentVolunteers(null)}>
          <div className="volunteer-modal" onClick={(e) => e.stopPropagation()}>
            <div className="volunteer-modal-header">
              <h3>Volunteers for {selectedTournamentVolunteers['Tournament Name']}</h3>
              <button 
                className="close-btn"
                onClick={() => setSelectedTournamentVolunteers(null)}
              >
                ×
              </button>
            </div>
            <div className="volunteer-modal-body">
              {selectedTournamentVolunteers.volunteers && selectedTournamentVolunteers.volunteers.length > 0 ? (
                <ul className="volunteer-list">
                  {selectedTournamentVolunteers.volunteers.map((volunteer, idx) => {
                    // volunteer is an object with Volunteer Name and Comments
                    const volunteerName = volunteer['Volunteer Name'] || (typeof volunteer === 'string' ? volunteer : '');
                    const comments = volunteer['Comments'] || '';
                    
                    return (
                      <li key={idx} className="volunteer-item">
                        <span className="volunteer-name">👤 {volunteerName}</span>
                        {comments && <span className="volunteer-comment">💬 {comments}</span>}
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p className="no-volunteers-msg">No volunteers registered yet</p>
              )}
            </div>
          </div>
        </div>
      )}

      {showAddForm && (
        <div className="add-tournament-form">
          <h3>Add New Tournament</h3>
          <form onSubmit={handleAddTournament}>
            <div className="form-row">
              <div className="form-group">
                <label>Tournament Name *</label>
                <input
                  type="text"
                  name="tournament_name"
                  value={formData.tournament_name}
                  onChange={handleInputChange}
                  placeholder="e.g., Summer Championship"
                  disabled={loading}
                  required
                />
              </div>

              <div className="form-group">
                <label>Start Date *</label>
                <input
                  type="date"
                  name="start_date"
                  value={formData.start_date}
                  onChange={handleInputChange}
                  disabled={loading}
                  required
                />
              </div>

              <div className="form-group">
                <label>End Date</label>
                <input
                  type="date"
                  name="end_date"
                  value={formData.end_date}
                  onChange={handleInputChange}
                  disabled={loading}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Venue</label>
                <input
                  type="text"
                  name="venue"
                  value={formData.venue}
                  onChange={handleInputChange}
                  placeholder="Location"
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label>Tournament URL (for live tracking)</label>
                <input
                  type="text"
                  name="tournament_url"
                  value={formData.tournament_url}
                  onChange={handleInputChange}
                  placeholder="https://badmintonsweden.tournamentsoftware.com/tournament/..."
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label>Status</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  disabled={loading}
                >
                  <option value="Upcoming">Upcoming</option>
                  <option value="Ongoing">Ongoing</option>
                  <option value="Completed">Completed</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Start Time</label>
                <TimeInput
                  value={formData.start_time}
                  onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                  label="Start Time"
                />
              </div>

              <div className="form-group">
                <label>End Time</label>
                <TimeInput
                  value={formData.end_time}
                  onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                  label="End Time"
                />
              </div>
            </div>

            <button 
              type="submit" 
              className="btn-submit"
              disabled={loading}
            >
              {editingTournament ? (loading ? 'Updating...' : 'Update Tournament') : (loading ? 'Adding...' : 'Add Tournament')}
            </button>

            {editingTournament && (
              <button 
                type="button"
                className="btn-cancel"
                onClick={() => {
                  setEditingTournament(null);
                  setFormData({
                    tournament_name: '',
                    start_date: '',
                    end_date: '',
                    venue: '',
                    start_time: '',
                    end_time: '',
                    status: 'Upcoming'
                  });
                }}
                disabled={loading}
              >
                Cancel Edit
              </button>
            )}
          </form>

          {editingTournament && (
            <div className="volunteers-management-section">
              <h4>📋 Manage Volunteers for {editingTournament}</h4>
              
              <div className="volunteer-management-section">
                <div className="add-volunteer-form">
                  <h5>Add Volunteer</h5>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Volunteer Name *</label>
                      <select
                        value={selectedVolunteer}
                        onChange={(e) => setSelectedVolunteer(e.target.value)}
                        disabled={loading || availableVolunteers.length === 0}
                        className="volunteer-dropdown"
                      >
                        <option value="">-- Select a volunteer --</option>
                        {availableVolunteers.map((volunteer) => (
                          <option key={volunteer} value={volunteer}>
                            {volunteer}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Comments (optional)</label>
                      <input
                        type="text"
                        value={volunteerComment}
                        onChange={(e) => setVolunteerComment(e.target.value)}
                        placeholder="e.g., Experience level, notes"
                        maxLength="200"
                        disabled={loading}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={handleAddVolunteerToTournament}
                      className="btn-add-volunteer"
                      disabled={loading}
                    >
                      + Add
                    </button>
                  </div>
                </div>

                <div className="current-volunteers">
                  <h5>Current Volunteers</h5>
                  {editingTournament && tournaments.find(t => t['Tournament Name'] === editingTournament)?.volunteers && tournaments.find(t => t['Tournament Name'] === editingTournament).volunteers.length > 0 ? (
                    <ul className="volunteer-list-edit">
                      {tournaments.find(t => t['Tournament Name'] === editingTournament).volunteers.map((volunteer, idx) => {
                        const volunteerName = volunteer['Volunteer Name'] || (typeof volunteer === 'string' ? volunteer : '');
                        const comments = volunteer['Comments'] || '';
                        return (
                          <li key={idx} className="volunteer-item-edit">
                            <div className="volunteer-info">
                              <span className="volunteer-name">👤 {volunteerName}</span>
                              {comments && <span className="volunteer-comment">💬 {comments}</span>}
                            </div>
                            <button
                              type="button"
                              onClick={() => handleRemoveVolunteerFromTournament(volunteerName)}
                              className="btn-remove-volunteer"
                              disabled={loading}
                            >
                              Remove
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <p className="no-volunteers-msg">No volunteers registered yet</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {loading && tournaments.length === 0 ? (
        <div className="loading">Loading tournaments...</div>
      ) : tournaments.length === 0 ? (
        <div className="empty-state">
          <p>No tournaments yet. Click "Add Tournament" to create one.</p>
        </div>
      ) : (
        <div className="tournaments-table-container">
          <table className="tournaments-table">
            <thead>
              <tr>
                <th>Tournament Name</th>
                <th>Start Date</th>
                <th>End Date</th>
                <th>Venue</th>
                <th>Time</th>
                <th>Volunteers</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {tournaments.map((tournament, idx) => (
                <tr key={idx}>
                  <td className="tournament-name">{tournament['Tournament Name']}</td>
                  <td>{tournament['Start Date']}</td>
                  <td>{tournament['End Date']}</td>
                  <td>{tournament.Venue || '-'}</td>
                  <td className="time">
                    {tournament['Start Time']} - {tournament['End Time'] || 'TBA'}
                  </td>
                  <td className="volunteers-cell">
                    {tournament.volunteer_count > 0 ? (
                      <div 
                        className="volunteer-info"
                        onClick={() => setSelectedTournamentVolunteers(tournament)}
                        style={{ cursor: 'pointer' }}
                      >
                        <span className="volunteer-badge">{tournament.volunteer_count}</span>
                        <span className="volunteer-list-btn">👥</span>
                      </div>
                    ) : (
                      <span className="no-volunteers">-</span>
                    )}
                  </td>
                  <td>
                    <span className={`status-badge ${tournament.Status?.toLowerCase()}`}>
                      {tournament.Status || 'Upcoming'}
                    </span>
                  </td>
                  <td className="actions">
                    <button
                      className="btn btn-edit"
                      onClick={() => handleEditTournament(tournament)}
                      title="Edit tournament"
                    >
                      ✏️
                    </button>
                    <button
                      className="btn btn-delete"
                      onClick={() => handleDeleteTournament(tournament['Tournament Name'])}
                      title="Delete tournament"
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Import from Badminton Sweden - multi-select modal */}
      {showImportModal && (
        <div className="modal-overlay" onClick={() => !importLoading && setShowImportModal(false)}>
          <div className="modal import-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>🔍 Import from Badminton Sweden</h2>
              <button className="close-btn" onClick={() => setShowImportModal(false)} disabled={importLoading}>×</button>
            </div>
            <p className="import-desc">Select the tournaments you want to add. Name, dates, venue and link are filled in automatically.</p>

            {importLoading && importResults.length === 0 ? (
              <p className="import-loading">🔄 Searching open tournaments...</p>
            ) : importResults.length === 0 ? (
              <p className="import-empty">No open tournaments found.</p>
            ) : (
              <>
                <div className="import-select-all">
                  <label>
                    <input
                      type="checkbox"
                      checked={importResults.length > 0 && importResults.every((_, i) => selectedImports[i])}
                      onChange={toggleImportSelectAll}
                    />
                    Select all ({importResults.length})
                  </label>
                </div>
                <div className="import-list">
                  {importResults.map((t, idx) => (
                    <label key={idx} className={`import-item ${selectedImports[idx] ? 'selected' : ''}`}>
                      <input
                        type="checkbox"
                        checked={!!selectedImports[idx]}
                        onChange={() => toggleImportSelect(idx)}
                      />
                      <div className="import-item-info">
                        <div className="import-item-name">{t.name}</div>
                        <div className="import-item-meta">
                          📍 {t.location || '—'} · 📅 {t.date_start}{t.date_end && t.date_end !== t.date_start ? ` → ${t.date_end}` : ''}
                          {!t.url && <span className="import-nolink"> · ⚠️ no link</span>}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
                <div className="import-actions">
                  <span>{Object.values(selectedImports).filter(Boolean).length} selected</span>
                  <button
                    className="btn-confirm-import"
                    onClick={handleConfirmImport}
                    disabled={importLoading || Object.values(selectedImports).filter(Boolean).length === 0}
                  >
                    {importLoading ? 'Adding...' : '➕ Add Selected'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ManageTournaments;
